import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/PageHeader';
import { apiFetch, ApiError } from '@/lib/api';
import { useT } from '@/lib/i18n';

interface CreateResp {
  tenant: { id: string };
}

export function TenantNew() {
  const t = useT();
  const nav = useNavigate();
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const resp = await apiFetch<CreateResp>('/tenants', {
        method: 'POST',
        body: JSON.stringify({
          id: id.toLowerCase().trim(),
          name: name.trim(),
          contactEmail: contactEmail.trim() || undefined,
          primaryAdmin: {
            email: adminEmail.trim().toLowerCase(),
            password: adminPassword,
            displayName: displayName.trim() || undefined,
          },
        }),
      });
      nav(`/superadmin/tenants/${resp.tenant.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('superadmin.tenantNew.title')}
        subtitle={t('superadmin.tenantNew.subtitle')}
      />
      <Card>
        <CardContent className="p-6">
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-2xl"
            onSubmit={submit}
          >
            <div className="space-y-1">
              <Label htmlFor="t-id">{t('superadmin.tenantNew.id')}</Label>
              <Input
                id="t-id"
                value={id}
                onChange={e => setId(e.target.value)}
                placeholder="acme"
                required
                pattern="[a-z0-9-]{1,32}"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="t-name">{t('superadmin.tenantNew.name')}</Label>
              <Input
                id="t-name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="t-contact">
                {t('superadmin.tenantNew.contactEmail')}
              </Label>
              <Input
                id="t-contact"
                type="email"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1 sm:col-span-2 pt-2 text-sm font-medium text-muted-foreground">
              {t('superadmin.tenantNew.primaryAdmin')}
            </div>
            <div className="space-y-1">
              <Label htmlFor="a-email">
                {t('superadmin.tenantNew.adminEmail')}
              </Label>
              <Input
                id="a-email"
                type="email"
                value={adminEmail}
                onChange={e => setAdminEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="a-pw">
                {t('superadmin.tenantNew.adminPassword')}
              </Label>
              <Input
                id="a-pw"
                type="password"
                value={adminPassword}
                onChange={e => setAdminPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="a-name">
                {t('superadmin.tenantNew.adminDisplayName')}
              </Label>
              <Input
                id="a-name"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
              />
            </div>
            {err && (
              <div className="sm:col-span-2 text-sm text-destructive">
                {err}
              </div>
            )}
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? t('superadmin.tenantNew.submitting')
                  : t('superadmin.tenantNew.submit')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => nav('/superadmin/tenants')}
              >
                {t('superadmin.tenantNew.cancel')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
